import React, { useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import {
  audienceSyncOptions,
  audienceTypeOptions,
  audienceUserTypeOptions,
} from '@luxuryescapes/contract-svc-promo/audience';
import { getRegions } from '@luxuryescapes/lib-regions';

import DateTimeWidget from '~/components/Common/Elements/DateTimeWidget';
import PageHeader from '~/components/Common/Elements/PageHeader';
import Spinner from '~/components/Common/Spinner';
import AccordionSection from '~/components/v2/common/Accordion/Accordion';

import { useUsersByIds } from '~/hooks/apis/users/useUsersByIds';
import useCurrentBrand from '~/hooks/useCurrentBrand';

import { AudienceAdminOutput, CreateAudienceAdminPayload } from '~/services/PromoService';
import { getDayJs } from '~/services/TimeService';

import {
  useCreateAudienceAdmin,
  useCreateAudienceUsersAdmin,
  useDeleteAudienceUsersAdmin,
  useGetAudienceAdminList,
  useGetAudienceUsersAdminList,
  useUpdateAudienceAdmin,
} from '../../hooks/apis/promo/useAudiences';

import AudienceSummary from './AudienceSummary';
import AudienceTesterWidget from './AudienceTesterWidget';
import AudiencesListTable from './AudiencesListTable';

type AudienceAdminRecord = AudienceAdminOutput;

type AudienceAdminViewRecord = AudienceAdminRecord & {
  modifiedBy?: string;
  updatedAt?: string;
};

type AudienceFormValues = {
  audienceName: string;
  enabled: boolean;
  syncModel: CreateAudienceAdminPayload['syncModel'];
  audienceType: CreateAudienceAdminPayload['audienceType'];
  audienceStartDate: string;
  audienceOverrideExpiresAt: string;
  audienceOverrideExpiresAtEnabled: boolean;
  region: Array<string>;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmTerm: string;
  userType: NonNullable<CreateAudienceAdminPayload['config']['userType']> | '';
  isLuxPlusMember: boolean;
  requiresUserId: boolean;
  userIdBucket: string;
};

type AudienceWritePayload = CreateAudienceAdminPayload & {
  brand: string;
};

type AudienceUserType = NonNullable<CreateAudienceAdminPayload['config']['userType']>;
type AudienceUsersFormValues = {
  userIds: string;
  numberToDelete: string;
  dryRun: boolean;
};

function splitList(values?: Array<string>): string {
  return values?.join(', ') ?? '';
}

function getRegionOptions(brand: string) {
  return getRegions(brand as Parameters<typeof getRegions>[0]).map((region) => ({
    code: region.code,
    name: region.name,
  }));
}

export function toList(value: string | Array<string>): Array<string> | undefined {
  const items = Array.isArray(value)
    ? value.map((item) => item.trim()).filter(Boolean)
    : value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);

  return items.length > 0 ? items : undefined;
}

function toAudienceUserType(value: string): AudienceUserType | undefined {
  const normalizedValue = value.trim();

  if (!normalizedValue) {
    return undefined;
  }

  return audienceUserTypeOptions.find((option) => option.key === normalizedValue)?.key;
}

function mapAudienceToFormValues(audience: AudienceAdminRecord | undefined): AudienceFormValues {
  const config = audience?.config;
  return {
    audienceName: audience?.audienceName ?? '',
    enabled: audience?.enabled ?? true,
    syncModel: audience?.syncModel ?? 'manual',
    audienceType: audience?.audienceType ?? 'staticList',
    audienceStartDate: audience?.audienceStartDate ?? '',
    audienceOverrideExpiresAt: audience?.audienceOverrideExpiresAt ?? '',
    audienceOverrideExpiresAtEnabled: Boolean(audience?.audienceOverrideExpiresAt),
    region: config?.region ?? [],
    utmSource: splitList(config?.utm?.source),
    utmMedium: splitList(config?.utm?.medium),
    utmCampaign: splitList(config?.utm?.campaign),
    utmTerm: splitList(config?.utm?.term),
    userType: (config?.userType ?? '') as AudienceFormValues['userType'],
    isLuxPlusMember: Boolean(config?.conditions?.user?.isLuxPlusMember),
    requiresUserId: Boolean(config?.conditions?.user?.requiresUserId),
    userIdBucket: config?.conditions?.user?.userIdBucket ?? '',
  };
}

export function buildAudiencePayload(values: AudienceFormValues, brand: string): AudienceWritePayload {
  return {
    brand,
    audienceName: values.audienceName.trim(),
    audienceType: values.audienceType,
    syncModel: values.syncModel,
    enabled: values.enabled,
    audienceStartDate: values.audienceStartDate || null,
    audienceOverrideExpiresAt: values.audienceOverrideExpiresAtEnabled
      ? values.audienceOverrideExpiresAt || null
      : null,
    config: {
      region: toList(values.region),
      utm: {
        source: toList(values.utmSource),
        medium: toList(values.utmMedium),
        campaign: toList(values.utmCampaign),
        term: toList(values.utmTerm),
      },
      userType: toAudienceUserType(values.userType),
      conditions:
        values.isLuxPlusMember || values.requiresUserId || values.userIdBucket.trim()
          ? {
              user: {
                ...(values.isLuxPlusMember ? { isLuxPlusMember: true } : {}),
                ...(values.requiresUserId ? { requiresUserId: true } : {}),
                ...(values.userIdBucket.trim() ? { userIdBucket: values.userIdBucket.trim() } : {}),
              },
            }
          : undefined,
    },
  };
}

function splitUserIds(value: string): Array<string> {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function getMutationErrorMessage(error: unknown, fallbackMessage: string): string {
  return error instanceof Error && error.message ? error.message : fallbackMessage;
}

function getUserDisplayName(user: Record<string, unknown> | undefined, fallbackId: string): string {
  if (!user) {
    return fallbackId;
  }

  const givenName = typeof user.givenName === 'string' ? user.givenName.trim() : '';
  const surname = typeof user.surname === 'string' ? user.surname.trim() : '';
  const fullName = [givenName, surname].filter(Boolean).join(' ');
  if (fullName) {
    return fullName;
  }

  if (typeof user.full_name === 'string' && user.full_name.trim()) {
    return user.full_name.trim();
  }

  if (typeof user.fullName === 'string' && user.fullName.trim()) {
    return user.fullName.trim();
  }

  if (typeof user.email === 'string' && user.email.trim()) {
    return user.email.trim();
  }

  return fallbackId;
}

export default function AudienceAdminPage() {
  const params = useParams<{ audienceId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const brand = useCurrentBrand();

  const audienceId = params.audienceId;
  const isEdit = location.pathname.endsWith('/edit');
  const isCreate = !audienceId;

  const audienceListQuery = useGetAudienceAdminList({
    query: { brand, enabled: 'all', page: 0, pageSize: 500, sortOrder: 'asc' },
    enabled: !isCreate,
  });
  const audienceUsersQuery = useGetAudienceUsersAdminList({
    query: { brand, audienceId, enabled: 'all', page: 0, pageSize: 100 },
    enabled: !isCreate && !!audienceId,
  });

  const audience = useMemo(
    () => audienceListQuery.data?.result.audienceList.find((item) => item.audienceId === audienceId),
    [audienceId, audienceListQuery.data],
  ) as AudienceAdminViewRecord | undefined;
  const auditUserIds = useMemo(
    () => Array.from(new Set([audience?.createdBy, audience?.modifiedBy].filter((value): value is string => !!value))),
    [audience?.createdBy, audience?.modifiedBy],
  );
  const auditUsersQuery = useUsersByIds({
    brand,
    userIds: auditUserIds,
    enabled: !isCreate && auditUserIds.length > 0,
  });
  const auditUsersById = useMemo(() => {
    const entries = (auditUsersQuery.data ?? []).flatMap((user) => {
      const userRecord = user as Record<string, unknown>;
      const ids = [userRecord.id_member, userRecord.memberId, userRecord.id_account, userRecord.userId].filter(
        (value): value is string => typeof value === 'string' && value.trim().length > 0,
      );

      return ids.map((id) => [id, user] as const);
    });

    return new Map(entries);
  }, [auditUsersQuery.data]);

  const audienceUserRows = audienceUsersQuery.data?.result?.audienceUsers ?? [];
  const regionOptions = useMemo(() => getRegionOptions(brand), [brand]);

  const { control, handleSubmit, reset, setValue, clearErrors } = useForm<AudienceFormValues>({
    defaultValues: mapAudienceToFormValues(undefined),
  });
  const {
    control: audienceUsersControl,
    handleSubmit: handleAudienceUsersSubmit,
    reset: resetAudienceUsers,
  } = useForm<AudienceUsersFormValues>({
    defaultValues: { userIds: '', numberToDelete: '1', dryRun: true },
  });

  const audienceOverrideExpiresAtEnabled = useWatch({ control, name: 'audienceOverrideExpiresAtEnabled' });
  const createAudienceUsersDryRun = useWatch({ control: audienceUsersControl, name: 'dryRun' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (isCreate) {
      reset(mapAudienceToFormValues(undefined));
      return;
    }

    if (audience) {
      reset(mapAudienceToFormValues(audience));
    }
  }, [audience, isCreate, reset]);

  useEffect(() => {
    if (isCreate) {
      resetAudienceUsers({ userIds: '', numberToDelete: '1', dryRun: true });
    }
  }, [isCreate, resetAudienceUsers]);

  useEffect(() => {
    if (!audienceOverrideExpiresAtEnabled) {
      setValue('audienceOverrideExpiresAt', '');
      clearErrors('audienceOverrideExpiresAt');
    }
  }, [audienceOverrideExpiresAtEnabled, clearErrors, setValue]);

  const createMutation = useCreateAudienceAdmin();
  const updateMutation = useUpdateAudienceAdmin();
  const addAudienceUsersMutation = useCreateAudienceUsersAdmin();
  const deleteAudienceUsersMutation = useDeleteAudienceUsersAdmin();

  const audienceUserColumns = useMemo(
    () => [
      { key: 'audienceUserId', label: 'Audience user ID' },
      { key: 'userId', label: 'User ID' },
      { key: 'sessionId', label: 'Session ID' },
      { key: 'createdAt', label: 'Created at' },
      { key: 'updatedAt', label: 'Updated at' },
    ],
    [],
  );

  const audienceUsersSection = !isCreate ? (
    <Stack spacing={3} sx={{ mt: 3 }}>
      <AudiencesListTable
        title="Audience users"
        description="Users currently assigned to this audience."
        rows={audienceUserRows}
        isLoading={audienceUsersQuery.isLoading}
        isError={audienceUsersQuery.isError}
        errorMessage={
          audienceUsersQuery.error instanceof Error ? audienceUsersQuery.error.message : 'Failed to load audience users'
        }
        emptyMessage="No audience users found."
        columns={audienceUserColumns}
      />
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={3} component="form">
          <Box>
            <Typography variant="h6">Add audience users</Typography>
            <Typography variant="body2" color="text.secondary">
              Paste a comma-separated list of user IDs to add to this audience.
            </Typography>
          </Box>
          <Controller
            control={audienceUsersControl}
            name="userIds"
            rules={{ required: 'Enter at least one user ID' }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="User IDs"
                helperText={fieldState.error?.message ?? 'Comma-separated user IDs'}
                error={!!fieldState.error}
                fullWidth
                multiline
                minRows={3}
              />
            )}
          />
          <Button
            variant="contained"
            onClick={handleAudienceUsersSubmit(async (values) => {
              if (!audienceId) {
                return;
              }
              try {
                await addAudienceUsersMutation.mutateAsync({
                  brand,
                  audienceId,
                  userIds: splitUserIds(values.userIds),
                });
                enqueueSnackbar('Audience users added', { variant: 'success' });
                resetAudienceUsers({ userIds: '', numberToDelete: '1', dryRun: true });
              } catch (error) {
                enqueueSnackbar(getMutationErrorMessage(error, 'Failed to add audience users'), {
                  variant: 'error',
                });
              }
            })}
            disabled={addAudienceUsersMutation.isPending}
          >
            Add users
          </Button>
        </Stack>
      </Paper>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={3} component="form">
          <Box>
            <Typography variant="h6">Delete audience users</Typography>
            <Typography variant="body2" color="text.secondary">
              Delete users in bulk from this audience. Start with dry run before committing.
            </Typography>
          </Box>
          <Controller
            control={audienceUsersControl}
            name="numberToDelete"
            rules={{
              validate: (value) => {
                const parsed = Number.parseInt(value, 10);
                return Number.isInteger(parsed) && parsed > 0 ? true : 'Enter a whole number greater than zero';
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Number to delete"
                type="number"
                error={!!fieldState.error}
                helperText={fieldState.error?.message ?? 'How many users to delete'}
                fullWidth
              />
            )}
          />
          <Controller
            control={audienceUsersControl}
            name="dryRun"
            render={({ field }) => (
              <FormControlLabel
                control={<Switch checked={field.value} onChange={(event) => field.onChange(event.target.checked)} />}
                label="Preview only"
              />
            )}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleAudienceUsersSubmit(async (values) => {
              if (!audienceId) {
                return;
              }

              if (!values.dryRun) {
                setDeleteDialogOpen(true);
                return;
              }

              try {
                await deleteAudienceUsersMutation.mutateAsync({
                  brand,
                  audienceId,
                  enabled: 'all',
                  numberToDelete: Number.parseInt(values.numberToDelete, 10),
                  dryRun: true,
                });
                enqueueSnackbar('Dry run completed', { variant: 'success' });
              } catch (error) {
                enqueueSnackbar(getMutationErrorMessage(error, 'Failed to run audience users preview'), {
                  variant: 'error',
                });
              }
            })}
            disabled={deleteAudienceUsersMutation.isPending}
          >
            {createAudienceUsersDryRun ? 'Run preview' : 'Delete users'}
          </Button>
        </Stack>
      </Paper>
    </Stack>
  ) : null;

  const onSubmit = async (values: AudienceFormValues) => {
    if (isEdit) {
      if (!audienceId) {
        enqueueSnackbar('Missing audience id', { variant: 'error' });
        return;
      }
      try {
        await updateMutation.mutateAsync({ audienceId, payload: buildAudiencePayload(values, brand) });
        enqueueSnackbar('Audience updated', { variant: 'success' });
        navigate('/marketing/audiences');
      } catch (error) {
        enqueueSnackbar(getMutationErrorMessage(error, 'Failed to update audience'), { variant: 'error' });
      }
      return;
    }

    try {
      const response = await createMutation.mutateAsync(buildAudiencePayload(values, brand));
      enqueueSnackbar('Audience created', { variant: 'success' });
      navigate(`/marketing/audiences/${response.result.audience.audienceId}/edit`);
    } catch (error) {
      enqueueSnackbar(getMutationErrorMessage(error, 'Failed to create audience'), { variant: 'error' });
    }
  };

  if (!isCreate && audienceListQuery.isLoading) {
    return <Spinner />;
  }

  if (!isCreate && audienceListQuery.isError) {
    return (
      <Container maxWidth="xl">
        <PageHeader title="Audience" backButton="/marketing/audiences" />
        <Alert severity="error">
          {audienceListQuery.error instanceof Error ? audienceListQuery.error.message : 'Failed to load audience'}
        </Alert>
      </Container>
    );
  }

  if (!isCreate && !audience) {
    return (
      <Container maxWidth="xl">
        <PageHeader title="Audience" backButton="/marketing/audiences" />
        <Alert severity="warning">Audience not found.</Alert>
      </Container>
    );
  }

  const pageTitle = isCreate
    ? 'Create Audience'
    : isEdit
      ? `Edit Audience: ${audience?.audienceName}`
      : audience?.audienceName;

  const hasUtmValues = Boolean(
    audience?.config?.utm?.source?.length ||
    audience?.config?.utm?.medium?.length ||
    audience?.config?.utm?.campaign?.length ||
    audience?.config?.utm?.term?.length,
  );

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <Container maxWidth="xl">
        <PageHeader title={pageTitle} backButton="/marketing/audiences" />

        {!isCreate && !isEdit ? (
          <Stack spacing={3} sx={{ mt: 3 }}>
            <AudienceSummary audience={audience} />
            <Button component={Link} to={`/marketing/audiences/${audienceId}/edit`} variant="contained">
              Edit audience
            </Button>
          </Stack>
        ) : (
          <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={3}>
                <Controller
                  control={control}
                  name="audienceName"
                  rules={{ required: 'Audience name is required' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Audience name"
                      fullWidth
                      required
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="audienceStartDate"
                  render={({ field, fieldState }) => (
                    <DateTimeWidget
                      value={field.value}
                      onChange={field.onChange}
                      label="Audience start date"
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message ?? 'Optional'}
                    />
                  )}
                />
                <Stack spacing={2}>
                  <Controller
                    control={control}
                    name="audienceOverrideExpiresAtEnabled"
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            onChange={(event) => field.onChange(event.target.checked)}
                            onBlur={field.onBlur}
                            inputRef={field.ref}
                          />
                        }
                        label="Add audience end date"
                      />
                    )}
                  />
                  {audienceOverrideExpiresAtEnabled ? (
                    <Controller
                      control={control}
                      name="audienceOverrideExpiresAt"
                      rules={{
                        validate: (value) => {
                          if (!audienceOverrideExpiresAtEnabled) {
                            return true;
                          }
                          const trimmed = value.trim();
                          if (!trimmed) {
                            return 'Please enter an audience end date, or turn off the toggle.';
                          }
                          if (!getDayJs(trimmed).isValid()) {
                            return 'Please enter a valid date and time.';
                          }
                          return true;
                        },
                      }}
                      render={({ field, fieldState }) => (
                        <DateTimeWidget
                          value={field.value}
                          onChange={field.onChange}
                          label="Audience override expires at"
                          error={Boolean(fieldState.error)}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  ) : null}
                </Stack>
                <AccordionSection title="Audience config" columns={1} defaultExpanded={false}>
                  <Stack spacing={3}>
                    <Typography variant="body2" color="text.secondary">
                      Settings that define the audience type and how it is maintained.
                    </Typography>
                    <TextField label="Brand" value={brand} fullWidth InputProps={{ readOnly: true }} />
                    <Controller
                      control={control}
                      name="audienceType"
                      render={({ field }) => (
                        <TextField {...field} label="Audience type" fullWidth select>
                          {audienceTypeOptions.map((option) => (
                            <MenuItem key={option.key} value={option.key}>
                              {option.title}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                    <Controller
                      control={control}
                      name="syncModel"
                      render={({ field }) => (
                        <TextField {...field} label="Sync model" fullWidth select>
                          {audienceSyncOptions.map((option) => (
                            <MenuItem key={option.key} value={option.key}>
                              {option.title}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                    <Controller
                      control={control}
                      name="enabled"
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch checked={field.value} onChange={(event) => field.onChange(event.target.checked)} />
                          }
                          label="Enabled"
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="userType"
                      render={({ field }) => (
                        <TextField {...field} label="User type" helperText="Optional" fullWidth select>
                          <MenuItem value="">
                            <Typography component="span" sx={{ fontStyle: 'italic' }}>
                              None
                            </Typography>
                          </MenuItem>
                          {audienceUserTypeOptions.map((option) => (
                            <MenuItem key={option.key} value={option.key}>
                              {option.title}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                    <Controller
                      control={control}
                      name="region"
                      render={({ field }) => (
                        <Stack spacing={1}>
                          <InputLabel id="audience-region-label">Region values</InputLabel>
                          <TextField
                            select
                            SelectProps={{
                              multiple: true,
                              labelId: 'audience-region-label',
                              input: <OutlinedInput label="Region values" />,
                              renderValue: (selected) => (Array.isArray(selected) ? selected.join(', ') : ''),
                            }}
                            fullWidth
                            {...field}
                            onChange={(event) => {
                              field.onChange(
                                typeof event.target.value === 'string'
                                  ? event.target.value.split(',')
                                  : (event.target.value as Array<string>),
                              );
                            }}
                          >
                            {regionOptions.map((region) => (
                              <MenuItem key={region.code} value={region.code}>
                                {region.code} - {region.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Stack>
                      )}
                    />
                  </Stack>
                </AccordionSection>
                <AccordionSection title="User conditions" columns={1} defaultExpanded={false}>
                  <Stack spacing={3}>
                    <Typography variant="body2" color="text.secondary">
                      Optional write-only targeting rules stored in config.conditions.user.
                    </Typography>
                    <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
                      <Controller
                        control={control}
                        name="isLuxPlusMember"
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={field.value}
                                onChange={(event) => field.onChange(event.target.checked)}
                              />
                            }
                            label="Require Lux Plus member"
                          />
                        )}
                      />
                      <Controller
                        control={control}
                        name="requiresUserId"
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={field.value}
                                onChange={(event) => field.onChange(event.target.checked)}
                              />
                            }
                            label="Require user ID"
                          />
                        )}
                      />
                    </Stack>
                    <Controller
                      control={control}
                      name="userIdBucket"
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="User ID bucket"
                          helperText="Optional bucket used by the API condition matcher"
                          fullWidth
                        />
                      )}
                    />
                  </Stack>
                </AccordionSection>
                <AccordionSection title="UTM targeting" columns={1} defaultExpanded={hasUtmValues}>
                  <Stack spacing={3}>
                    <Typography variant="body2" color="text.secondary">
                      When a user lands on the website with these utm params set, they will be considered a member of
                      this audience.
                    </Typography>
                    <Controller
                      control={control}
                      name="utmSource"
                      render={({ field }) => (
                        <TextField {...field} label="UTM source values" helperText="Comma-separated values" fullWidth />
                      )}
                    />
                    <Controller
                      control={control}
                      name="utmMedium"
                      render={({ field }) => (
                        <TextField {...field} label="UTM medium values" helperText="Comma-separated values" fullWidth />
                      )}
                    />
                    <Controller
                      control={control}
                      name="utmCampaign"
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="UTM campaign values"
                          helperText="Comma-separated values"
                          fullWidth
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="utmTerm"
                      render={({ field }) => (
                        <TextField {...field} label="UTM term values" helperText="Comma-separated values" fullWidth />
                      )}
                    />
                  </Stack>
                </AccordionSection>
                {!isCreate ? (
                  <AccordionSection title="Audience metadata" columns={1} defaultExpanded={false}>
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary">
                        Brand: {brand}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Created at: {audience?.createdAt ?? '—'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Created by:{' '}
                        {getUserDisplayName(auditUsersById.get(audience?.createdBy ?? ''), audience?.createdBy ?? '—')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Updated at: {audience?.updatedAt ?? '—'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Updated by:{' '}
                        {getUserDisplayName(
                          auditUsersById.get(audience?.modifiedBy ?? ''),
                          audience?.modifiedBy ?? '—',
                        )}
                      </Typography>
                    </Stack>
                  </AccordionSection>
                ) : null}
                <Stack direction="row" spacing={2}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={createMutation.isPending || updateMutation.isPending}
                  >
                    {isEdit ? 'Save changes' : 'Create audience'}
                  </Button>
                  <Button component={Link} to="/marketing/audiences" variant="text">
                    Cancel
                  </Button>
                </Stack>
              </Stack>
            </form>
          </Paper>
        )}
        {!isCreate && !isEdit ? audienceUsersSection : null}
        {!isCreate && !isEdit ? <AudienceTesterWidget defaultAudienceId={audienceId} /> : null}
      </Container>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete audience users?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete the selected number of users from this audience. Confirm only if you intend to
            run the real delete, not a preview.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} variant="text">
            Cancel
          </Button>
          <Button
            onClick={handleAudienceUsersSubmit(async (values) => {
              if (!audienceId) {
                return;
              }

              try {
                await deleteAudienceUsersMutation.mutateAsync({
                  brand,
                  audienceId,
                  enabled: 'all',
                  numberToDelete: Number.parseInt(values.numberToDelete, 10),
                  dryRun: false,
                });
                setDeleteDialogOpen(false);
                enqueueSnackbar('Audience users deleted', { variant: 'success' });
              } catch (error) {
                enqueueSnackbar(getMutationErrorMessage(error, 'Failed to delete audience users'), {
                  variant: 'error',
                });
              }
            })}
            variant="contained"
            color="primary"
          >
            Delete users
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
